/**
 * Onglet « Préférences » de la page Compte — affichage (largeur de page, thème) et
 * notifications.
 *
 * Pas de bouton « Enregistrer » : chaque interaction applique le changement immédiatement
 * (`usePreferences().save`) et déclenche un `PUT /api/me/preferences` en arrière-plan, corps
 * complet (contrat serveur, pas de correctif partiel). `SaveStatus` reflète trois états :
 * chargement, non enregistré (aucun réglage explicite côté serveur — `updatedAt` vaut `null`),
 * enregistré.
 *
 * Clés API et Aide MCP ont leur propre onglet (`AccountPage`) : ce panneau ne porte plus que
 * l'affichage et les notifications (§ audit exiUI 2026-09-27).
 */

import { useTranslation } from 'react-i18next';
import {
  NOTIFY_DIGESTS,
  NOTIFY_EVENTS,
  PAGE_WIDTHS,
  THEME_MODES,
  TIME_OF_DAY_PATTERN,
} from '@exiplan/shared-types';

import { BoutonRadio, CaseACocher, GroupeChoix } from '@exidis/ui/composants';

import { Empty, Eyebrow, SaveStatus } from '../components/common.jsx';
import { usePreferences } from '../hooks/usePreferences.js';
import {
  CELLS_PLANCHE,
  CELL_PLANCHE,
  FORM_FIELD,
  FORM_LABEL,
  FORM_ROW,
  INPUT_SM,
} from '../ui/theme.js';

/** Littéraux dans l'objet — même convention que `TeamsPage::ROLE_KEYS`. */
const PAGE_WIDTH_LABEL_KEYS = {
  pleine: 'preferences.display.pageWidth.pleine',
  1440: 'preferences.display.pageWidth.1440',
  1280: 'preferences.display.pageWidth.1280',
  1024: 'preferences.display.pageWidth.1024',
};

const DIGEST_LABEL_KEYS = {
  instant: 'preferences.notifications.digest.instant',
  daily: 'preferences.notifications.digest.daily',
  weekly: 'preferences.notifications.digest.weekly',
};

/** Une famille d'événement → sa clé de préférence booléenne et sa clé de libellé. */
const EVENT_FIELDS = {
  'task.assigned': {
    field: 'notifyTaskAssigned',
    labelKey: 'preferences.notifications.events.taskAssigned',
  },
  'task.due': { field: 'notifyTaskDue', labelKey: 'preferences.notifications.events.taskDue' },
  'comment.mention': {
    field: 'notifyCommentMention',
    labelKey: 'preferences.notifications.events.commentMention',
  },
  'comment.reply': {
    field: 'notifyCommentReply',
    labelKey: 'preferences.notifications.events.commentReply',
  },
  'project.shared': {
    field: 'notifyProjectShared',
    labelKey: 'preferences.notifications.events.projectShared',
  },
  'org.membership': {
    field: 'notifyOrgMembership',
    labelKey: 'preferences.notifications.events.orgMembership',
  },
};

/**
 * Un `<input type="time">` vidé ou saisi au clavier passe par des valeurs intermédiaires —
 * `''` en premier lieu. Les envoyer telles quelles produisait un 400 garanti sur une
 * interaction parfaitement normale, puisque le serveur exige `HH:MM`. On n'enregistre donc
 * que les valeurs complètes ; l'utilisateur continue de voir ce qu'il tape, le champ étant
 * natif et non contrôlé sur ce point.
 */
const MOTIF_HEURE = new RegExp(TIME_OF_DAY_PATTERN);
const estHeureComplete = (valeur) => MOTIF_HEURE.test(valeur);

export function PreferencesPage() {
  const { t } = useTranslation();
  const { preferences, updatedAt, loading, error, saving, save } = usePreferences();

  const saveState = loading ? 'loading' : updatedAt ? 'saved' : 'unsaved';

  if (loading) {
    return <p className="p-4 text-sm text-ink-soft">{t('preferences.loading')}</p>;
  }

  if (error) {
    return (
      <div className="p-4">
        <Empty>{t('preferences.loadError', { message: error.message })}</Empty>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <SaveStatus state={saveState} busy={saving} />
      </div>

      <div className={`${CELLS_PLANCHE} grid-cols-1`}>
        <div className={`${CELL_PLANCHE} bg-paper p-6`}>
          <span className="ticks" aria-hidden="true" />
          <Eyebrow>{t('preferences.display.legend')}</Eyebrow>

          {/* Radios carrés exiUI (BoutonRadio, reference.md §6.2 ter) : plus de radio rond natif. */}
          <GroupeChoix legende={t('preferences.display.pageWidth.legend')} className="mt-3">
            {PAGE_WIDTHS.map((width) => (
              <BoutonRadio
                key={width}
                name="pageWidth"
                value={width}
                checked={preferences.pageWidth === width}
                onChange={() => save({ pageWidth: width })}
                label={t(PAGE_WIDTH_LABEL_KEYS[width])}
              />
            ))}
          </GroupeChoix>

          <GroupeChoix legende={t('preferences.display.theme.legend')} className="mt-4">
            {THEME_MODES.map((mode) => (
              <BoutonRadio
                key={mode}
                name="theme"
                value={mode}
                checked={preferences.theme === mode}
                onChange={() => save({ theme: mode })}
                label={t(`theme.${mode}`)}
              />
            ))}
          </GroupeChoix>
        </div>

        <div className={`${CELL_PLANCHE} bg-paper p-6`}>
          <span className="ticks" aria-hidden="true" />
          <Eyebrow>{t('preferences.notifications.legend')}</Eyebrow>

          {/* Repliée par défaut : rien de ces réglages n'est câblé côté envoi aujourd'hui, ça ne
            mérite pas la moitié de l'écran (§ audit exiUI 2026-09-27).
            1.4.0 : plus de bande encadrée d'accent — exiUI n'a pas d'encart d'alerte, et l'accent
            est réservé à l'action et à l'état actif. Cadre neutre (fantôme), résumé en eyebrow
            gris, chevron `ChevronBas` qui pivote à l'ouverture. */}
          <details className="group mt-3 border-2 border-line">
            <summary className="flex cursor-pointer list-none items-center gap-1.5 px-3 py-2 font-mono text-[0.68rem] tracking-eyebrow text-ink-faint uppercase hover:bg-line-soft [&::-webkit-details-marker]:hidden">
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="square"
                className="h-3.5 w-3.5 shrink-0 transition-transform group-open:rotate-180"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
              <span>{t('preferences.notifications.warningSummary')}</span>
            </summary>
            <p className="px-3 pb-3 text-sm text-ink-soft">
              {t('preferences.notifications.warning')}
            </p>
          </details>

          <GroupeChoix
            legende={t('preferences.notifications.channels.legend')}
            disposition="colonne"
            className="mt-4"
          >
            <CaseACocher
              checked={preferences.notifyEmail}
              onChange={(event) => save({ notifyEmail: event.target.checked })}
              label={t('preferences.notifications.channels.email')}
            />
            <CaseACocher
              checked={preferences.notifyInApp}
              onChange={(event) => save({ notifyInApp: event.target.checked })}
              label={t('preferences.notifications.channels.inApp')}
            />
          </GroupeChoix>

          <GroupeChoix legende={t('preferences.notifications.digest.legend')} className="mt-4">
            {NOTIFY_DIGESTS.map((digest) => (
              <BoutonRadio
                key={digest}
                name="notifyDigest"
                value={digest}
                checked={preferences.notifyDigest === digest}
                onChange={() => save({ notifyDigest: digest })}
                label={t(DIGEST_LABEL_KEYS[digest])}
              />
            ))}
          </GroupeChoix>

          <GroupeChoix
            legende={t('preferences.notifications.events.legend')}
            disposition="colonne"
            className="mt-4"
          >
            {NOTIFY_EVENTS.map((event) => {
              const { field, labelKey } = EVENT_FIELDS[event];
              return (
                <CaseACocher
                  key={event}
                  checked={preferences[field]}
                  onChange={(changeEvent) => save({ [field]: changeEvent.target.checked })}
                  label={t(labelKey)}
                />
              );
            })}
          </GroupeChoix>

          <GroupeChoix
            legende={t('preferences.notifications.quietHours.legend')}
            disposition="colonne"
            className="mt-4"
          >
            <CaseACocher
              checked={preferences.quietHoursEnabled}
              onChange={(event) => save({ quietHoursEnabled: event.target.checked })}
              label={t('preferences.notifications.quietHours.enabled')}
            />
            {/* Ligne de formulaire exiUI (FORM_ROW) : deux champs `INPUT_SM`, libellés au même haut,
                champs au même bas — plus de champ `time` bordé à la main. */}
            <div className={FORM_ROW}>
              <label className={FORM_FIELD}>
                <span className={FORM_LABEL}>
                  {t('preferences.notifications.quietHours.start')}
                </span>
                <input
                  type="time"
                  value={preferences.quietHoursStart}
                  disabled={!preferences.quietHoursEnabled}
                  onChange={(event) =>
                    estHeureComplete(event.target.value) &&
                    save({ quietHoursStart: event.target.value })
                  }
                  className={INPUT_SM}
                />
              </label>
              <label className={FORM_FIELD}>
                <span className={FORM_LABEL}>{t('preferences.notifications.quietHours.end')}</span>
                <input
                  type="time"
                  value={preferences.quietHoursEnd}
                  disabled={!preferences.quietHoursEnabled}
                  onChange={(event) =>
                    estHeureComplete(event.target.value) &&
                    save({ quietHoursEnd: event.target.value })
                  }
                  className={INPUT_SM}
                />
              </label>
            </div>
          </GroupeChoix>
        </div>
      </div>
    </div>
  );
}
